 import { Cancel } from "@mui/icons-material";
import { Card, CircularProgress, FormControl, MenuItem, Select } from "@mui/material";
import ImagePicker from "components/ApnaUploader";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import SkLoading from "components/SkLoading";
import AstrieskIcon from "components/AstrieskIcon";
import { useMaterialUIController } from "context";
import PropTypes from "prop-types";
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { handleAlert } from "redux/festures/alertSlice";
import { getBrands, updateBrands, createBrands } from "redux/festures/brandSlice";
import { getGlobalCategory, getGlobalCategoryAppend } from "redux/festures/categorySlice";

const CreateBrand = ({ isOpen, isOpenUpdate, setIsOpenUpdate, setIsOpen }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const dispatch = useDispatch();
  const admin = localStorage.getItem("admin_id");

  // Brand states
  const [isBrandName, setIsBrandName] = useState("");
  const [isBrandImage, setIsBrandImage] = useState(null);
  const [isShow, setIsShow] = useState("");
  const [categoryId, setCategoryId] = useState("");

  // Scroll pagination states for category dropdown
  const [catPage, setCatPage] = useState(1);
  const [catTotalPages, setCatTotalPages] = useState(1);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Redux state
  const { Loading, singleBrands } = useSelector((data) => ({ ...data.isBrand }));
  const { category, isPages } = useSelector((state) => state.isCategory);

  // Fetch first page of categories on mount
  useEffect(() => {
    if (admin) {
      setCatPage(1);
      dispatch(getGlobalCategory({ adminId: admin, filter: "&disable=false", page: 1, limit: 10 }));
    }
  }, [admin, dispatch]);

  // Update local totalPages when Redux isPages changes
  useEffect(() => {
    if (isPages) {
      setCatTotalPages(isPages);
    }
  }, [isPages]);

  // Prefill in update mode
  useEffect(() => {
    if (singleBrands && isOpenUpdate) {
      setIsBrandName(singleBrands?.name || "");
      setIsShow(singleBrands?.image || "");
      setIsBrandImage(null);
      setCategoryId(singleBrands?.categoryId?._id || singleBrands?.categoryId || "");
    } else {
      setIsBrandName("");
      setIsShow("");
      setIsBrandImage(null);
      setCategoryId("");
    }
  }, [singleBrands, isOpenUpdate]);

  // Generate preview URL for newly selected image
  const imagePreviewUrl = useMemo(() => {
    if (isBrandImage && isBrandImage instanceof File) {
      return URL.createObjectURL(isBrandImage);
    }
    return null;
  }, [isBrandImage]);

  // Cleanup object URL on unmount or change
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
    };
  }, [imagePreviewUrl]);

  // Infinite scroll handler for Select dropdown
  const handleDropdownScroll = useCallback(
    (event) => {
      const listEl = event.target;
      const bottom = listEl.scrollHeight - listEl.scrollTop <= listEl.clientHeight + 20;
      if (bottom && !isFetchingMore && catPage < catTotalPages) {
        const nextPage = catPage + 1;
        setIsFetchingMore(true);
        setCatPage(nextPage);
        dispatch(
          getGlobalCategoryAppend({ adminId: admin, filter: "&disable=false", page: nextPage, limit: 10 })
        ).then(() => {
          setIsFetchingMore(false);
        });
      }
    },
    [catPage, catTotalPages, isFetchingMore, admin, dispatch]
  );

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (isBrandName && (isBrandImage || isShow)) {
      const formData = new FormData();
      formData.append("name", isBrandName);
      if (categoryId) formData.append("categoryId", categoryId);
      if (isBrandImage) formData.append("image", isBrandImage);

      if (isOpenUpdate) {
        dispatch(
          updateBrands({
            url: `${process.env.REACT_APP_APII}/updatebrand/${singleBrands?._id}/${admin}`,
            data: formData,
          })
        ).then((data) => {
          dispatch(
            handleAlert({
              isOpen: true,
              type: `${data?.payload?.success ? "success" : "error"}`,
              msg: data?.payload?.message,
            })
          );
          setIsOpen(false);
          setIsOpenUpdate(false);
          resetForm();
          dispatch(getBrands(`/getAllBrands`));
        });
      } else {
        dispatch(
          createBrands({
            url: `${process.env.REACT_APP_APII}/createBrand/${admin}`,
            data: formData,
          })
        ).then((data) => {
          dispatch(
            handleAlert({
              isOpen: true,
              type: `${data?.payload?.success ? "success" : "error"}`,
              msg: data?.payload?.message,
            })
          );
          setIsOpen(false);
          setIsOpenUpdate(false);
          resetForm();
          dispatch(getBrands(`/getAllBrands`));
        });
      }
    } else {
      dispatch(
        handleAlert({
          isOpen: true,
          type: "warning",
          msg: "All fields are required",
        })
      );
    }
  };

  const resetForm = () => {
    setIsBrandName("");
    setIsBrandImage(null);
    setIsShow("");
    setCategoryId("");
  };

  if (Loading) return <SkLoading />;

  return (
    <MDBox
      display="flex"
      alignItems="center"
      sx={{
        justifyContent: "center",
        flexDirection: "column",
        gap: 2.5,
        width: "100%",
      }}
    >
      {/* Header */}
      <Card
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 1.2,
          boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
        }}
      >
        <MDTypography variant="h6" sx={{ color: darkMode ? "white" : "#1a237e", fontWeight: "bold" }}>
          {isOpenUpdate ? "Update Brand" : "Create Brand"}
        </MDTypography>
      </Card>

      {/* Form Card */}
      <MDBox
        component="form"
        role="form"
        onSubmit={handleFormSubmit}
        sx={({ palette: { dark, white } }) => ({
          border: "1px solid",
          borderColor: darkMode ? "rgba(255,255,255,0.15)" : "#e0e6ed",
          borderRadius: 3,
          p: { xs: 2, sm: 3 },
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        })}
      >
        {/* Select Category */}
        <MDBox>
          <MDTypography variant="subtitle2" sx={{ color: darkMode ? "white" : "#1a237e", fontWeight: "bold", mb: 1 }}>
            Select Category <AstrieskIcon />
          </MDTypography>
          <FormControl fullWidth>
            <Select
              required
              displayEmpty
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              MenuProps={{
                PaperProps: {
                  onScroll: handleDropdownScroll,
                  style: { maxHeight: 250 },
                },
              }}
              sx={{
                height: 45,
                borderRadius: "8px",
                color: darkMode ? "white" : "inherit",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: darkMode ? "rgba(255,255,255,0.3)" : "#e0e6ed",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: darkMode ? "white" : "#1a237e",
                },
                "& .MuiSvgIcon-root": {
                  color: darkMode ? "white" : "inherit",
                },
              }}
            >
              <MenuItem value="" disabled>
                <span style={{ color: "#9e9e9e" }}>-- Select a Category --</span>
              </MenuItem>
              {category && category.length > 0 &&
                category.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              {isFetchingMore && (
                <MenuItem disabled sx={{ justifyContent: "center" }}>
                  <CircularProgress size={20} color="info" />
                </MenuItem>
              )}
              {catPage >= catTotalPages && category && category.length > 0 && (
                <MenuItem disabled sx={{ justifyContent: "center" }}>
                  <MDTypography variant="caption" sx={{ color: "#9e9e9e" }}>
                    All categories loaded
                  </MDTypography>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </MDBox>

        {/* Brand Name */}
        <MDBox>
          <MDTypography variant="subtitle2" sx={{ color: darkMode ? "white" : "#1a237e", fontWeight: "bold", mb: 1 }}>
            Brand Name <AstrieskIcon />
          </MDTypography>
          <MDInput
            required
            type="text"
            placeholder="Enter brand name"
            fullWidth
            value={isBrandName}
            onChange={(e) => {
              const value = e.target.value;
              if (/^[A-Za-z ]*$/.test(value)) {
                setIsBrandName(value);
              }
            }}
          />
        </MDBox>

        {/* Brand Image */}
        <MDBox>
          <MDTypography variant="subtitle2" sx={{ color: darkMode ? "white" : "#1a237e", fontWeight: "bold", mb: 1 }}>
            Brand Image{" "}
            <MDTypography variant="caption" component="span" sx={{ color: "#9e9e9e", fontWeight: "normal" }}>
              &nbsp; ( image size - 247 × 247 px )
            </MDTypography>
          </MDTypography>

          <ImagePicker
            name="Brand Image"
            multiple={false}
            images={isBrandImage}
            setImages={(files) => {
              setIsBrandImage(files);
              setIsShow("");
            }}
          />

          {/* New Image Preview */}
          {isBrandImage && imagePreviewUrl && (
            <MDBox mt={2} display="flex" alignItems="flex-start" gap={1}>
              <MDBox
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: darkMode ? "2px solid rgba(255,255,255,0.5)" : "2px solid #1a237e",
                  flexShrink: 0,
                }}
              >
                <img
                  src={imagePreviewUrl}
                  alt="brand preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </MDBox>
              <Cancel
                onClick={() => setIsBrandImage(null)}
                sx={{
                  cursor: "pointer",
                  color: "#9e9e9e",
                  fontSize: 20,
                  mt: -0.5,
                  "&:hover": { color: "#f44336" },
                }}
              />
            </MDBox>
          )}

          {/* Existing / Server Image Preview (Update Mode) */}
          {!isBrandImage && isShow && (
            <MDBox mt={2} display="flex" alignItems="flex-start" gap={1}>
              <MDBox
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: "12px",
                  overflow: "hidden",
                  border: darkMode ? "2px solid rgba(255,255,255,0.5)" : "2px solid #1a237e",
                  flexShrink: 0,
                }}
              >
                <img
                  src={
                    isShow?.startsWith("http")
                      ? isShow
                      : `${process.env.REACT_APP_URI}${isShow}`
                  }
                  alt="existing brand"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </MDBox>
              <Cancel
                onClick={() => setIsShow("")}
                sx={{
                  cursor: "pointer",
                  color: "#9e9e9e",
                  fontSize: 20,
                  mt: -0.5,
                  "&:hover": { color: "#f44336" },
                }}
              />
            </MDBox>
          )}
        </MDBox>

        {/* Submit Button */}
        <MDBox display="flex" justifyContent="flex-end">
          <MDButton color="info" variant="gradient" type="submit">
            {Loading ? (
              <CircularProgress color="inherit" size={20} />
            ) : isOpenUpdate ? (
              "Update Brand"
            ) : (
              "Create Brand"
            )}
          </MDButton>
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

export default CreateBrand;

CreateBrand.propTypes = {
  isOpenUpdate: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.any,
  setIsOpenUpdate: PropTypes.any,
  isOpen: PropTypes.any,
};
